import { Button } from '@mm/components';
import { KnownSiteType } from '@mm/stateful-components/ChatWidget/InstallationGuide/constants';
import classNames from 'classnames';
import React from 'react';
import styles from './CustomerChatInstallationGuideSlideoutMenu.module.css';
import wordressBannerSrc from './wordpress-banner.jpg';
import bigCommerceStepThree from 'assets/installation-guides/bigCommerce_step-3.png';
import bigCommerceStepFour from 'assets/installation-guides/bigCommerce_step-4.png';
import bigCommerceStepFive from 'assets/installation-guides/bigCommerce_step-5.png';
import bigCommerceStepSix from 'assets/installation-guides/bigCommerce_step-6.png';
import bigCommerceStepSeven from 'assets/installation-guides/bigCommerce_step-7.png';

export type CustomerChatInstallationGuideSlideoutMenuInstructionsType = Record<
  KnownSiteType,
  | null
  | (
      | string
      | {
          step: React.ReactNode;
          includeCode?: boolean;
          includeCode2?: boolean;
          image?: string;
          substeps?: string[];
        }
    )[]
>;
// tslint:disable max-line-length
const COMMON_STEP_3 = {
  step: 'In the "Install Website Chat" section, add your website URL under "What is your website URL". Then, copy out the Javascript code under "Install code on Website"',
  image:
    'https://files.helpdocs.io/jlaj445u4q/articles/ml56dt0x0x/1604949252535/image.png',
};
const COMMON_FINAL_STEP = {
  step: 'When you are ready to go live, go into your Customer Chat Widget in Customers.ai and click Activate (at the top of the page)',
  image:
    'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1591219013985/image.png',
};
export const CustomerChatInstallationGuideSlideoutMenuInstructions: CustomerChatInstallationGuideSlideoutMenuInstructionsType =
  {
    WordPress: [
      'Download the latest version of the plugin from https://wordpress.org/plugins/wp-chatbot/',
      {
        step: 'In the WordPress site go to Plugins -> Add New',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ah23c2nyzk/1561654730406/image.png',
      },
      {
        step: 'Click Upload Plugin',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ah23c2nyzk/1561654790637/image.png',
      },
      {
        step: 'Click on Choose File',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ah23c2nyzk/1561654845753/image.png',
      },
      "Select .zip file you've just downloaded and hit Install Now",
      {
        step: "Once you see 'Plugin installed successfully.' message click on Activate Plugin",
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ah23c2nyzk/1561655164005/image.png',
      },
      {
        step: 'Congratulations, the plugin is successfully installed! Now at the bottom of plugin list you should see WP-Chatbot',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ah23c2nyzk/1561655304319/image.png',
      },
    ],
    'Google Tag Manager': [
      COMMON_STEP_3,
      {
        step: `Login to Google Tag Manager and Navigate to Tags > Add A New Tag.`,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1529009165043/image-png',
      },
      'Select “Custom HTML” as your tag type.',
      {
        step: 'Paste the following code',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1529009311773/image-png',
      },
      {
        step: 'Click "Choose a trigger" and select “All pages”',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1529007981193/image-png',
      },
      {
        step: 'Name and save your tag.',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1522885434600/image.png',
      },
      COMMON_FINAL_STEP,
    ],
    Duda: [
      COMMON_STEP_3,
      {
        step: 'Log into Duda and go to your Website Builder > Settings > Header HTML',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ml56dt0x0x/1603143546249/image.png',
      },
      {
        step: 'Paste the following code.',
        includeCode: true,
      },
      'Click Save',
      COMMON_FINAL_STEP,
    ],
    Leadpages: [
      COMMON_STEP_3,
      {
        step: 'Log into your Leadpages account and click on "Settings"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/7l6uqhjj7m/1591814186620/image.png',
      },
      {
        step: 'Under the Analytics tab, go to "Head Section Tracking Code" and paste the following code at the bottom of this section.',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/7l6uqhjj7m/1591814434624/image.png',
      },
      'Save your changes',
      COMMON_FINAL_STEP,
    ],
    BigCommerce: [
      {
        step: (
          <>
            Log in to your BigCommerce account, and{' '}
            <strong>go to the Storefront</strong> section of the dashboard.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/m6q0ijhibb/1731331725591/big-commerce-step-1.png',
      },
      {
        step: (
          <>
            Select <strong>Themes</strong> and click <strong>Customize</strong>{' '}
            button on your current theme
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/m6q0ijhibb/1731332013014/big-commerce-step-2.png',
      },
      {
        step: (
          <>
            Click on <strong>Edit Theme Files</strong> to access the file editor
            where you can make direct changes to the theme. You can find the
            button in different places.
          </>
        ),
        image: bigCommerceStepThree,
      },
      {
        step: (
          <>
            In the theme editor, find and open the <strong>Templates</strong>{' '}
            folder → <strong>Layout</strong> folder. Within Layout, open the
            file named <strong>base.html</strong>.
          </>
        ),
        image: bigCommerceStepFour,
      },
      {
        step: (
          <>
            <strong>Copy</strong> your pixel code and <strong>paste</strong> it{' '}
            <strong>under head tag</strong>.
          </>
        ),
        includeCode: true,
        image: bigCommerceStepFive,
      },
      {
        step: (
          <>
            Once you've added the code, click <strong>Save</strong> to confirm
            your changes.
          </>
        ),
        image: bigCommerceStepSix,
      },
      {
        step: (
          <>
            Return to the main <strong>Themes page</strong> and click{' '}
            <strong>Apply</strong> to activate the theme with your new changes.
            You can find the button in different places.
          </>
        ),
        image: bigCommerceStepSeven,
      },
    ],
    Other: [
      {
        step: 'In the "Install Website Chat" section, add your website URL under "What is your website URL".',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ml56dt0x0x/1604949252535/image.png',
      },
      {
        step: (
          <>
            Paste the code below immediately after the{' '}
            <strong>&lt;head&gt;</strong> tag on every page you want your chat
            widget to appear.{' '}
          </>
        ),
        includeCode: true,
      },
    ],
    Shopify: [
      COMMON_STEP_3,
      {
        step: 'In your Shopify go to your Online Store Theme Editor',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687445377691/image-156.png',
      },
      {
        step: 'Click “Edit code”',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687443945267/image-157.png',
      },
      {
        step: 'Find "theme.liquid"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687444027512/image-158.png',
      },
      {
        step: 'Paste your Chat Widget Javascript code directly below <head>',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687444043718/image-159.png',
      },
      {
        step: (
          <>
            Click <strong>“Not required”</strong> for permissions.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/16hdqqv4tu/1734360474710/screenshot-2024-12-12-at-11-51-10-am.png',
      },
      {
        step: (
          <>
            Click <strong>“Save”</strong> button at the top.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1731330020686/shopify-step-8.jpg',
      },
      {
        step: (
          <>
            Click <strong>“Connect” </strong>to activate your pixel.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1731330296147/shopify-step-9.jpg',
      },
    ],
    Squarespace: [
      COMMON_STEP_3,
      {
        step: 'Log into your Squarespace account. On the home menu, click on Settings > Advanced > Code Injection',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/h61b88tnxa/1603219016624/image.png',
      },
      {
        step: 'Paste the following code above under the "Header" section.',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/h61b88tnxa/1603219458053/image.png',
      },
      'Click on Save at the top left hand corner',
      COMMON_FINAL_STEP,
    ],
    Weebly: [
      COMMON_STEP_3,
      {
        step: 'Log into Weebly and "Edit Website".',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/hsrpirbjc3/1604951164264/image.png',
      },
      {
        step: 'Click on "Settings" > SEO, then paste the following code at the end of the "Header Code" section:',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/hsrpirbjc3/1596639588498/image.png',
      },
      'Click "Save". Then "Publish".',
      COMMON_FINAL_STEP,
    ],
    WiX: [
      COMMON_STEP_3,
      {
        step: 'Log into your WiX account and click on "Settings"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1604007003311/image.png',
      },
      {
        step: 'Under the "Advanced Settings" section, click on "Tracking Tools". Then, click on "New Tools" button at the top right hand corner and select "Custom"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1604007808340/image.png',
      },
      {
        step: 'Paste the following code in the text box labeled "Paste the code snippet here"',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1586901128639/image.png',
        substeps: ['Make sure code is added for All Pages.'],
      },
      {
        step: 'Save & make sure your custom code is toggled to active in WiX',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1604008049481/image.png',
      },
      COMMON_FINAL_STEP,
    ],
  };
const isSemrush = location.pathname.startsWith('/semrush');
export const EMAIL_DETECTOR_COMMON_FINAL_STEP = {
  step: isSemrush
    ? 'Please activate the pixel by switching the toggle on'
    : 'When you are ready to go live, go into your X-Ray Lead Generation Pixel in Customers.ai and click Activate (at the top of the trigger editor)',
};
export const EmailDetectorInstallationGuideSlideoutMenuInstructions: CustomerChatInstallationGuideSlideoutMenuInstructionsType =
  {
    WordPress: [
      {
        step: (
          <>
            <span>Install our WordPress plugin to your desired website.</span>
            <Button
              id="btn-wordpress-plugin"
              onClick={() =>
                window.open(
                  'https://wordpress.org/plugins/mobilemonkey-x-ray-installer',
                  '_blank',
                )
              }
              css={{ container: classNames(styles.stepButton) }}
            >
              <span className="brand-name">Customers.ai</span>&nbsp;WordPress
              Plugin
            </Button>
          </>
        ),
        image: wordressBannerSrc,
      },
      {
        step: 'Paste the following code in the plugin.',
        includeCode: true,
      },
      {
        step: 'Click Save Settings',
      },
      {
        step: 'If your WordPress website is using any cache plugins, please remember to clear all plugin caches for changes to take effect',
      },
    ],
    'Google Tag Manager': [
      {
        step: (
          <>
            <span>
              {`Login to Google Tag Manager and Navigate to Tags > Add A New Tag`}
            </span>
            <Button
              id="btn-google-tag-manager"
              onClick={() =>
                window.open('https://tagmanager.google.com', '_blank')
              }
              css={{ container: styles.stepButton }}
            >
              Go to Google Tag Manager
            </Button>
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1529009165043/image-png',
      },
      {
        step: 'Select “Custom HTML” as your tag type.',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1529009311773/image-png',
      },
      {
        step: 'Paste the following code',
        includeCode: true,
      },
      {
        step: 'Click "Choose a trigger" and select “All pages”',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1529007981193/image-png',
      },
      {
        step: 'Name and save your tag.',
        image: isSemrush
          ? undefined
          : 'https://files.helpdocs.io/jlaj445u4q/articles/ldwem5tjkn/1522885434600/image.png',
      },
      EMAIL_DETECTOR_COMMON_FINAL_STEP,
    ],
    Duda: [
      {
        step: 'Log into Duda and go to your Website Builder > Settings > Header HTML',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/ml56dt0x0x/1603143546249/image.png',
      },
      {
        step: 'Paste the following code.',
        includeCode: true,
      },
      'Click Save',
      EMAIL_DETECTOR_COMMON_FINAL_STEP,
    ],
    Leadpages: [
      {
        step: 'Log into your Leadpages account and click on "Settings"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/7l6uqhjj7m/1591814186620/image.png',
      },
      {
        step: 'Under the Analytics tab, go to "Head Section Tracking Code" and paste the following code at the bottom of this section.',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/7l6uqhjj7m/1591814434624/image.png',
      },
      'Save your changes',
      EMAIL_DETECTOR_COMMON_FINAL_STEP,
    ],
    Other: [
      {
        step: (
          <>
            Paste the code below immediately after the{' '}
            <strong>&lt;head&gt;</strong> tag on the main index.html for single
            page applications or on every page for multi page applications
          </>
        ),
        includeCode: true,
      },
    ],
    Shopify: [
      {
        step: 'In your Shopify go to your Online Store Theme Editor',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687445377691/image-156.png',
      },
      {
        step: 'Click “Edit code”',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687443945267/image-157.png',
      },
      {
        step: 'Find "theme.liquid"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687444027512/image-158.png',
      },
      {
        step: 'Paste your X-Ray Pixel code snippet directly below <head>',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1687444043718/image-159.png',
      },
      {
        step: (
          <>
            Go to your <strong>Store Settings</strong> and click{' '}
            <strong>“Customer events”</strong>
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/m6q0ijhibb/1731330669620/shopify-step-1.jpg',
      },
      {
        step: (
          <>
            Click <strong>“Add custom pixel”</strong> and name it{' '}
            <strong>“Customers.ai”</strong>
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1718373276040/shopify-step-2.png',
      },
      {
        step: (
          <>
            <strong>Copy</strong> your pixel code and <strong>paste</strong> it
            into the <strong>“Code”</strong>field
          </>
        ),
        includeCode2: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/m6q0ijhibb/1731330745559/shopify-step-7.jpg',
      },
      {
        step: (
          <>
            Click <strong>“Not required”</strong> for permissions.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/16hdqqv4tu/1734360474710/screenshot-2024-12-12-at-11-51-10-am.png',
      },
      {
        step: (
          <>
            Click <strong>“Save”</strong> button at the top.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1731330020686/shopify-step-8.jpg',
      },
      {
        step: (
          <>
            Click <strong>“Connect” </strong>to activate your pixel.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/cuckyzsp7f/1731330296147/shopify-step-9.jpg',
      },
    ],
    Squarespace: [
      {
        step: 'Log into your Squarespace account. On the home menu, click on Settings > Advanced > Code Injection',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/h61b88tnxa/1603219016624/image.png',
      },
      {
        step: 'Paste the following code above under the "Header" section.',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/h61b88tnxa/1603219458053/image.png',
      },
      'Click on Save at the top left hand corner',
      EMAIL_DETECTOR_COMMON_FINAL_STEP,
    ],
    Weebly: [
      {
        step: 'Log into Weebly and "Edit Website".',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/hsrpirbjc3/1604951164264/image.png',
      },
      {
        step: 'Click on "Settings" > SEO, then paste the following code at the end of the "Header Code" section:',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/hsrpirbjc3/1596639588498/image.png',
      },
      'Click "Save". Then "Publish".',
      EMAIL_DETECTOR_COMMON_FINAL_STEP,
    ],
    WiX: [
      {
        step: 'Log into your WiX account and click on "Settings"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1604007003311/image.png',
      },
      {
        step: 'Under the "Advanced Settings" section, click on "Tracking Tools". Then, click on "New Tools" button at the top right hand corner and select "Custom"',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1604007808340/image.png',
      },
      {
        step: 'Paste the following code in the text box labeled "Paste the code snippet here"',
        includeCode: true,
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1586901128639/image.png',
        substeps: ['Make sure code is added for All Pages.'],
      },
      {
        step: 'Save & make sure your custom code is toggled to active in WiX',
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/jqwb3ajyvh/1604008049481/image.png',
      },
      EMAIL_DETECTOR_COMMON_FINAL_STEP,
    ],
    BigCommerce: [
      {
        step: (
          <>
            Log in to your BigCommerce account, and{' '}
            <strong>go to the Storefront</strong> section of the dashboard.
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/m6q0ijhibb/1731331725591/big-commerce-step-1.png',
      },
      {
        step: (
          <>
            Select <strong>Themes</strong> and click <strong>Customize</strong>{' '}
            button on your current theme
          </>
        ),
        image:
          'https://files.helpdocs.io/jlaj445u4q/articles/m6q0ijhibb/1731332013014/big-commerce-step-2.png',
      },
      {
        step: (
          <>
            Click on <strong>Edit Theme Files</strong> to access the file editor
            where you can make direct changes to the theme. You can find the
            button in different places.
          </>
        ),
        image: bigCommerceStepThree,
      },
      {
        step: (
          <>
            In the theme editor, find and open the <strong>Templates</strong>{' '}
            folder → <strong>Layout</strong> folder. Within Layout, open the
            file named <strong>base.html</strong>.
          </>
        ),
        image: bigCommerceStepFour,
      },
      {
        step: (
          <>
            <strong>Copy</strong> your pixel code and <strong>paste</strong> it{' '}
            <strong>under head tag</strong>.
          </>
        ),
        includeCode: true,
        image: bigCommerceStepFive,
      },
      {
        step: (
          <>
            Once you've added the code, click <strong>Save</strong> to confirm
            your changes.
          </>
        ),
        image: bigCommerceStepSix,
      },
      {
        step: (
          <>
            Return to the main <strong>Themes page</strong> and click{' '}
            <strong>Apply</strong> to activate the theme with your new changes.
            You can find the button in different places.
          </>
        ),
        image: bigCommerceStepSeven,
      },
    ],
  };
export type EmailDetectorInstallationGuideHowToVideoLinksType = null | Partial<
  Record<KnownSiteType, undefined | { title?: string; iframe?: string }>
>;
export const EmailDetectorInstallationGuideHowToVideoLinks: EmailDetectorInstallationGuideHowToVideoLinksType =
  {
    WordPress: {
      title: ' How to install X-Ray Pixel with WordPress plugin',
      iframe:
        'https://www.loom.com/embed/1e7ba6dc9d8f4027b6b71041e4038b0a?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true',
    },
    'Google Tag Manager': {
      title: 'How to install X-Ray Pixel with Google Tag Manager',
      iframe:
        'https://www.loom.com/embed/b63c850f18c94c1988f9d43f29e82c79?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true',
    },
    Other: {
      title: ' How to install X-Ray Pixel manually',
      iframe:
        'https://www.loom.com/embed/6b99cd7ac53a41fc836837c880ba6b0d?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true',
    },
  };
